import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Autocomplete,
  Typography,
  Stack,
  Grid2,
} from '@mui/material';
import { toast } from 'sonner';
import { Iconify } from '../Minimal/src/components/iconify';
import { TablePaginationCustom } from '../Minimal/src/components/pagination';
import { useTable } from '../Minimal/src/hooks/use-table';
import Header from '../Header';
import Footer from '../Footer';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import { format } from 'date-fns';
import { API_URL } from '../Config';
import config from "../common/Config";
import AccountService from '../../services/AccountServices';
import generatePaymentLedgerPDF from "./PaymentLadgerPDF";
import '../common/commonStyles.css';

const PaymentLadger = () => {
  const accessToken = localStorage.getItem('accessToken');
  const [dates, setDates] = useState([null, null]);
  const [reportrange, setReportrange] = useState('');

  const [userOptions, setUserOptions] = useState([]);
  const [userValue, setUserValue] = useState(null);

  const [paymentDetails, setPaymentDetails] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });
  const [paymentLedger, setPaymentLedger] = useState([]);
  const [nodataRow, setNodataRow] = useState('');

  const [totalCollect, setTotalCollect] = useState(0);
  const [cash, setCash] = useState(0);
  const [cheque, setCheque] = useState(0);
  const [card, setCard] = useState(0);

  const table = useTable({
    defaultRowsPerPage: 10,
    defaultDense: false,
    defaultOrder: 'desc',
    defaultOrderBy: 'date',
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const fetchSalesRapOptions = async () => {
    const apiSalesRapUrl = `${API_URL}/getSalesRep_or_driver`;
    try {
      const response = await fetch(apiSalesRapUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        }
      });

      const data = await response.json();

      const formattedOptions = data.data.map(item => ({
        label: `${item.first_name} ${item.last_name}`,
        value: item.id,
      }));

      setUserOptions(formattedOptions);
    } catch (error) {
      toast.error('Failed to fetch user list');
    }
  };

  const userHandleChange = (event, newValue) => {
    setUserValue(newValue);
  };

  const handleGeneratePDF = () => {
    generatePaymentLedgerPDF(paymentDetails, paymentLedger);
  };

  const searchFormData = () => {
    let startDate = dates[0] ? format(dates[0], 'MM/dd/yyyy') : '';
    let endDate = dates[1] ? format(dates[1], 'MM/dd/yyyy') : '';
    setReportrange(startDate ? startDate + ' - ' + endDate : '');

    let salesRapId = userValue ? userValue.value : '';

    if (!salesRapId) {
      toast.error('Please select user');
      return;
    }
    loadPaymentLadger(salesRapId, startDate, endDate);
  };

  const loadPaymentLadger = async (salesRapId, startDate, endDate) => {
    try {
      const data = await AccountService.fetchPaymentLadger(salesRapId, startDate, endDate, accessToken);
      if (Array.isArray(data.data) && data.data.length === 0) {
        setPaymentDetails({ name: '', startDate: '', endDate: '' });
        setPaymentLedger([]);
        setNodataRow('showinfo');
        table.onChangePage(null, 0);
        return;
      }
      if (data.data.customer_credit.id) {
        setPaymentDetails({
          name: data.data.customer_credit.first_name + ' ' + data.data.customer_credit.last_name,
          startDate: data.start_date,
          endDate: data.end_date
        });
        setPaymentLedger(data.data.customer_ledger);
        setNodataRow('');
        table.onChangePage(null, 0);
      }
    } catch (error) {
      toast.error('Failed to load payment ledger');
    }
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSalesRapOptions();

    document.title = 'Payment Ledger';
    return () => {
      document.title = 'Ebusso';
    };
  }, []);

  useEffect(() => {
    let total = 0;
    let cashTotal = 0;
    let chequeTotal = 0;
    let cardTotal = 0;

    paymentLedger.forEach(item => {
      const formattedAmount = parseFloat(item.collected_amount) || 0;
      total += formattedAmount;

      if (item.payment_mode === "cash") cashTotal += formattedAmount;
      else if (item.payment_mode === "card") cardTotal += formattedAmount;
      else if (item.payment_mode === "cheque") chequeTotal += formattedAmount;
    });

    setTotalCollect(total);
    setCash(cashTotal);
    setCheque(chequeTotal);
    setCard(cardTotal);
  }, [paymentLedger]);

  const renderRow = (item) => {
    const {
      date,
      name,
      collected_amount,
      payment_mode,
      account_no,
    } = item;
    const formattedAmount = parseFloat(collected_amount).toFixed(2);
    const formattedPaymentMode = payment_mode ? payment_mode : '';
    const formattedDate = date ? date : '';
    const formattedCompany = name ? name : '';
    const formattedaccount_no = account_no ? account_no : '';

    return (
      <TableRow
        key={`${formattedDate}-${formattedCompany}-${formattedAmount}`}
        className="table-cell-style"
      >
        <TableCell >{formattedDate}</TableCell>
        <TableCell>{formattedCompany} ({formattedaccount_no})</TableCell>
        <TableCell >{formattedAmount}</TableCell>
        <TableCell >{formattedPaymentMode}</TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Box className="page-container">
        <Paper
          elevation={0}
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2 container justifyContent="space-between" alignItems="center" spacing={2}>
              <Grid2 size={{ xs: 12, md: 4}}>
                <Typography className="page-title">
                  Payment Ledger
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={userOptions}
                  value={userValue}
                  onChange={userHandleChange}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className="input-border"
                      placeholder="Select User"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f5f7fa",
                          fontSize: "0.875rem",
                          "&:hover": {
                            backgroundColor: "#fff"
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#fff"
                          }
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      fontSize: "0.875rem",
                      "&:hover": {
                        backgroundColor: "#fff"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff"
                      }
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <CommonDatePicker
                  value={dates}
                  onChange={setDates}
                  className="input-border"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 2}} display="flex" gap={1}>
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={searchFormData}
                  fullWidth
                >
                  Submit
                </Button>
                {paymentDetails.name && (
                  <Button
                    variant="outlined"
                    startIcon={<Iconify icon="eva:printer-fill" width={16} />}
                    onClick={handleGeneratePDF}
                    className="button-variant-outlined"
                    fullWidth
                  >
                    Print
                  </Button>
                )}
              </Grid2>
            </Grid2>
          </Box>
          <Box sx={{ px: 2, py: 2 }}> 
          {paymentDetails.name && (
            <Box sx={{ mb: 2 }}>
              <Paper
                elevation={0}
                sx={{
                  border: "1px solid rgba(145, 158, 171, 0.16)",
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "#212b36", mb: 1 }}>
                  <strong>Period From:</strong> {paymentDetails.startDate} <strong>To:</strong> {paymentDetails.endDate}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#212b36" }}>
                  Total Collected: {totalCollect.toFixed(2)} | Cash: {cash.toFixed(2)} | Cheque: {cheque.toFixed(2)} | Card: {card.toFixed(2)}
                </Typography>
              </Paper>
            </Box>
          )}

          {paymentLedger.length > 0 && (
            <TableContainer
          
            >
              <Table
           
                className="table-cell-border"
              >
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell width="200px">Date</TableCell>
                    <TableCell width="300px">Customer</TableCell>
                    <TableCell width="150px">Amount</TableCell>
                    <TableCell width="150px">Payment Mode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentLedger
                    .slice(table.page * table.rowsPerPage, table.page * table.rowsPerPage + table.rowsPerPage)
                    .map((item) => renderRow(item))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {paymentLedger.length > 0 && (
            <Box sx={{ pt: 2 }}>
              <TablePaginationCustom
                className="pagination-styles"
                page={table.page}
                dense={table.dense}
                count={paymentLedger.length}
                rowsPerPage={table.rowsPerPage}
                onPageChange={table.onChangePage}
                onRowsPerPageChange={table.onChangeRowsPerPage}
              />
            </Box>
          )}

          {nodataRow && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body2" sx={{ color: "#d32f2f", fontSize: "0.875rem" }}>
                NO Transaction Available.
              </Typography>
            </Box>
          )}
             </Box>
        </Paper>
      </Box>
    </>
  );
};

export default PaymentLadger;
